/**
 * The sky's light table (ADR-141 and its amendment of 2026-10-31): the night levels, the colours of the turn and the
 * named overrides that replace the everyday sky for a rare event. Plain data and pure helpers, read by `ambientAt`, the
 * client's lighting and any system that wants to know the sky is not ordinary.
 */

/**
 * A colour as red, green and blue in 0 to 255.
 */
export type Rgb = readonly [number, number, number];

/**
 * The overrides in wire order: the everyday sky and the two named ones.
 */
export const SKY_OVERRIDES = Object.freeze(["none", "drained", "bloodMoon"] as const);

/**
 * The sky in force: `none` is the everyday curve; `drained` is the light drained from the sky (rare moon and world
 * events); `bloodMoon` is the very rare red sky. A server or GM hook switches an override on; none is an event system.
 */
export type SkyOverride = (typeof SKY_OVERRIDES)[number];

/**
 * Everything one sky needs: the night ambient on the brightest and the darkest night, the tint across the turn and the
 * share of the darkness layer a lit day keeps (`dayWash`).
 */
export interface SkyTable {
  readonly brightest: number;
  readonly darkest: number;
  readonly night: Rgb;
  readonly dawn: Rgb;
  readonly dusk: Rgb;
  readonly gold: Rgb;
  readonly dayWash: number;
}

/**
 * The tables, by override.
 */
export const SKIES: Readonly<Record<SkyOverride, SkyTable>> = {
  none: { brightest: 0.6, darkest: 0.5, night: [58, 78, 138], dawn: [205, 150, 170], dusk: [150, 96, 150], gold: [255, 146, 52], dayWash: 0 },
  drained: { brightest: 0.42, darkest: 0.35, night: [10, 24, 96], dawn: [176, 112, 150], dusk: [104, 58, 128], gold: [255, 146, 52], dayWash: 0 },
  bloodMoon: { brightest: 0.42, darkest: 0.35, night: [92, 20, 40], dawn: [170, 78, 84], dusk: [132, 40, 62], gold: [190, 24, 24], dayWash: 0.14 }
};

/**
 * Whether the sky is the blood moon, for any system that reacts to it.
 *
 * @param sky The sky in force.
 * @returns True under the blood moon.
 */
export function isBloodMoon(sky: SkyOverride): boolean {
  return sky === "bloodMoon";
}
